/**
 * RA2 dist datepicker logic: parse/format and calendar helpers.
 * Keeps same behavior as RA2 public/dist/datepicker.js.
 */

import {
  convertSeireki,
  convertWareki,
  convertWarekiDetail,
  convertWarekiMonth,
  convertSeirekiWrap,
} from '@/utils/wareki';

const FORMAT_PARTS = /(y|m|d)+/gi;
const REGEXP_DIGITS = /\d+/g;

export interface ParsedFormat {
  source: string;
  parts: string[];
  hasDay: boolean;
  hasMonth: boolean;
  hasYear: boolean;
}

export function parseFormat(formatStr: string): ParsedFormat {
  const source = String(formatStr).toLowerCase();
  const parts = source.match(FORMAT_PARTS) || [];
  const format: ParsedFormat = {
    source,
    parts,
    hasDay: false,
    hasMonth: false,
    hasYear: false,
  };
  parts.forEach(part => {
    if (part === 'dd' || part === 'd') format.hasDay = true;
    else if (part === 'mm' || part === 'm') format.hasMonth = true;
    else if (part === 'yyyy' || part === 'yy') format.hasYear = true;
  });
  return format;
}

export function getDaysInMonth(year: number, month: number): number {
  const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const days = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[month];
}

export function getMinDay(year: number, month: number, day: number): number {
  return Math.min(day, getDaysInMonth(year, month));
}

/**
 * holidays: { "YYYY-MM": ["01","15",...] } (RA2 CommonClass::getHolidays format)
 */
export function checkHolidays(
  holidays: Record<string, string[]> | undefined,
  day: string
): boolean {
  if (!holidays) return false;
  for (const key of Object.keys(holidays)) {
    if (holidays[key].indexOf(day) !== -1) return true;
  }
  return false;
}

export function checkHolidayForMonth(
  holidays: Record<string, string[]> | undefined,
  yearMonth: string,
  dayStr: string
): boolean {
  if (!holidays || !holidays[yearMonth]) return false;
  const chkday = dayStr.padStart(2, '0');
  return holidays[yearMonth].indexOf(chkday) !== -1 || holidays[yearMonth].indexOf(dayStr) !== -1;
}

/**
 * Parse date string (西暦 or 和暦) to Date. Dist: if contains 令和/平成/... convert_seireki_wrap first.
 */
export function parseDate(value: string | Date | null, format: ParsedFormat): Date {
  const now = new Date();
  let year = now.getFullYear();
  let month = now.getMonth();
  // 年月 / 年 のみのフォーマットは日を持たないため 1 日固定。今日の日付のままだと
  // new Date(2026, 1, 31) のように翌月へ繰り上がる。
  let day = format.hasDay ? now.getDate() : 1;

  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  if (typeof value !== 'string' || !value.trim()) {
    return new Date(year, month, day);
  }

  let str = value;
  if (/令和|平成|昭和|大正|明治/.test(str)) {
    str = convertSeirekiWrap(str);
  }
  const parts = str.match(REGEXP_DIGITS) || [];
  if (parts.length < format.parts.length) return new Date(year, month, day);

  format.parts.forEach((part, i) => {
    const num = parseInt(parts[i], 10) || (part === 'd' || part === 'dd' ? 1 : 1);
    switch (part) {
      case 'dd':
      case 'd':
        day = num;
        break;
      case 'mm':
      case 'm':
        month = num - 1;
        break;
      case 'yy':
        year = 2000 + num;
        break;
      case 'yyyy':
        year = num;
        break;
      default:
        break;
    }
  });

  return new Date(year, month, getMinDay(year, month, day));
}

/**
 * Format Date to display string (和暦 for year part in dist).
 */
export function formatDate(date: Date, format: ParsedFormat): string {
  const year = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const values: Record<string, string> = {
    d: String(d),
    dd: String(d).padStart(2, '0'),
    m: String(m),
    mm: String(m).padStart(2, '0'),
    yy: String(year).slice(2),
    yyyy: convertWarekiDetail(year, m, d),
  };
  let result = format.source;
  format.parts.forEach(part => {
    result = result.replace(part, values[part] ?? part);
  });
  return result;
}

export function formatDateWarekiWrap(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const wareki = convertWarekiDetail(y, date.getMonth() + 1, date.getDate());
  return `${wareki}年${m}月${d}日`;
}

/**
 * Parse a wareki shorthand digit string to a Date.
 *
 * The first digit selects the era:
 *   5 = 令和 (Reiwa, +2018)
 *   4 = 平成 (Heisei, +1988)
 *   3 = 昭和 (Showa, +1925)
 *   2 = 大正 (Taisho, +1911)
 *   1 = 明治 (Meiji, +1867)
 *
 * Day picker (7 digits): EYYMMDD
 *   e.g. "5070130" → Reiwa 7-01-30 → 2025-01-30
 *   e.g. "4300401" → Heisei 30-04-01 → 2018-04-01
 *   e.g. "3450720" → Showa 45-07-20 → 1970-07-20
 *
 * Month picker (5 digits): EYYMM
 *   e.g. "50701" → Reiwa 7-01 → 2025-01
 *
 * Also supports 8-digit seireki (YYYYMMDD) and 6-digit seireki month (YYYYMM).
 */
export function parseWarekiShorthand(
  input: string,
  mode: 'day' | 'month' | 'year' = 'day'
): Date | null {
  const s = input.replace(/\s/g, '');
  if (!/^\d+$/.test(s)) return null;

  const len = s.length;

  // 8-digit seireki day: YYYYMMDD
  if (mode === 'day' && len === 8) {
    const y = parseInt(s.slice(0, 4), 10);
    const m = parseInt(s.slice(4, 6), 10);
    const d = parseInt(s.slice(6, 8), 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      const date = new Date(y, m - 1, d);
      if (date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d) {
        return date;
      }
    }
    return null;
  }

  // 6-digit seireki month: YYYYMM (only when mode=month)
  if (mode === 'month' && len === 6) {
    const y = parseInt(s.slice(0, 4), 10);
    const m = parseInt(s.slice(4, 6), 10);
    if (m >= 1 && m <= 12) {
      return new Date(y, m - 1, 1);
    }
    return null;
  }

  // 4-digit seireki year: YYYY (only when mode=year)
  if (mode === 'year' && len === 4) {
    const y = parseInt(s, 10);
    if (y >= 1868 && y <= 2061) {
      return new Date(y, 0, 1);
    }
    return null;
  }

  // 7-digit wareki day: EYYMMDD (era prefix + 2-digit year + month + day)
  if (mode === 'day' && len === 7) {
    const eraDigit = parseInt(s[0], 10);
    const yy = parseInt(s.slice(1, 3), 10);
    const mm = parseInt(s.slice(3, 5), 10);
    const dd = parseInt(s.slice(5, 7), 10);
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
    return resolveByEraDigit(eraDigit, yy, mm, dd);
  }

  // 5-digit wareki month: EYYMM (era prefix + 2-digit year + month)
  if (mode === 'month' && len === 5) {
    const eraDigit = parseInt(s[0], 10);
    const yy = parseInt(s.slice(1, 3), 10);
    const mm = parseInt(s.slice(3, 5), 10);
    if (mm < 1 || mm > 12) return null;
    return resolveByEraDigit(eraDigit, yy, mm, 1);
  }

  // 3-digit wareki year: EYY (era prefix + 2-digit year)
  if (mode === 'year' && len === 3) {
    const eraDigit = parseInt(s[0], 10);
    const yy = parseInt(s.slice(1, 3), 10);
    return resolveByEraDigit(eraDigit, yy, 1, 1);
  }

  return null;
}

/** Era digit → offset mapping: 5=令和, 4=平成, 3=昭和, 2=大正, 1=明治 */
const ERA_OFFSETS: Record<number, number> = {
  5: 2018, // 令和
  4: 1988, // 平成
  3: 1925, // 昭和
  2: 1911, // 大正
  1: 1867, // 明治
};

const ERA_NAMES: Record<number, string> = {
  5: '令和',
  4: '平成',
  3: '昭和',
  2: '大正',
  1: '明治',
};

function resolveByEraDigit(eraDigit: number, yy: number, mm: number, dd: number): Date | null {
  const offset = ERA_OFFSETS[eraDigit];
  if (offset == null) return null;
  const gregorianYear = yy + offset;
  const candidate = new Date(gregorianYear, mm - 1, dd);
  // Verify the date didn't overflow (e.g., Feb 30 → Mar 2)
  if (
    candidate.getFullYear() !== gregorianYear ||
    candidate.getMonth() !== mm - 1 ||
    candidate.getDate() !== dd
  ) {
    return null;
  }
  return candidate;
}

/**
 * Progressive wareki formatting: converts raw digit input to a partial wareki
 * display string as the user types each segment.
 *
 * Day mode (EYYMMDD, 7 digits total):
 *   "5"       → "令和"
 *   "50"      → "令和0"
 *   "507"     → "令和07年"   (hideSuffix → "令和07")
 *   "5070"    → "令和07年0"
 *   "50701"   → "令和07年01月" (hideSuffix → "令和07年01")
 *   "507013"  → "令和07年01月3"
 *   "5070130" → "令和07年01月30日" (hideSuffix → "令和07年01月30")
 *
 * Month mode (EYYMM, 5 digits total):
 *   "5"       → "令和"
 *   "507"     → "令和07年"   (hideSuffix → "令和07")
 *   "50701"   → "令和07年01月" (hideSuffix → "令和07年01")
 *
 * @param hideSuffix When true, suppresses the trailing 年/月/日 at segment boundaries.
 *   This is used for the intermediate backspace step.
 * Returns null if the first digit isn't a valid era (1-5).
 */
export function formatWarekiProgressive(
  digits: string,
  mode: 'day' | 'month' | 'year' = 'day',
  hideSuffix = false
): string | null {
  if (!digits || digits.length === 0) return null;

  const eraDigit = parseInt(digits[0], 10);
  const eraName = ERA_NAMES[eraDigit];
  if (!eraName) return null;

  // 1 digit: era only
  if (digits.length === 1) return eraName;

  // 2 digits: era + partial year
  if (digits.length === 2) return `${eraName}${digits[1]}`;

  // 3+ digits: era + full year (only add 年 when year is in valid range for this era)
  const yearPart = digits.slice(1, 3);
  const yearNum = digits.length >= 3 ? parseInt(yearPart, 10) : 0;
  const yearValid = digits.length >= 3 && isWarekiYearValid(eraDigit, yearNum);
  const yearSuffix =
    digits.length === 3 ? (yearValid ? (hideSuffix ? '' : '年') : '') : yearValid ? '年' : '';

  if (mode === 'year') {
    if (digits.length >= 3) return `${eraName}${yearPart}${yearSuffix}`;
  }

  if (digits.length === 3) return `${eraName}${yearPart}${yearSuffix}`;

  // 4 digits: year + partial month (only add 年 if year was valid)
  if (digits.length === 4) return `${eraName}${yearPart}${yearValid ? '年' : ''}${digits[3]}`;

  // 5+ digits: year + full month (only add 月 when month 01-12)
  const monthPart = digits.slice(3, 5);
  const monthNum = digits.length >= 5 ? parseInt(monthPart, 10) : 0;
  const monthValid = digits.length >= 5 && isMonthValid(monthNum);
  const monthSuffix =
    digits.length === 5 ? (monthValid ? (hideSuffix ? '' : '月') : '') : monthValid ? '月' : '';

  if (mode === 'month') {
    if (digits.length >= 5)
      return `${eraName}${yearPart}${yearValid ? '年' : ''}${monthPart}${monthSuffix}`;
  }

  if (digits.length === 5)
    return `${eraName}${yearPart}${yearValid ? '年' : ''}${monthPart}${monthSuffix}`;

  // 6 digits: month + partial day (only add 月 if month was valid)
  if (digits.length === 6)
    return `${eraName}${yearPart}${yearValid ? '年' : ''}${monthPart}${monthValid ? '月' : ''}${digits[5]}`;

  // 7+ digits: only add 日 when day is 01-31
  const dayPart = digits.slice(5, 7);
  const dayNum = digits.length >= 7 ? parseInt(dayPart, 10) : 0;
  const dayValid = digits.length >= 7 && isDayValid(dayNum);
  const daySuffix =
    digits.length === 7 ? (dayValid ? (hideSuffix ? '' : '日') : '') : dayValid ? '日' : '';
  return `${eraName}${yearPart}${yearValid ? '年' : ''}${monthPart}${monthValid ? '月' : ''}${dayPart}${daySuffix}`;
}

/**
 * Returns whether the given digit count is at a suffix boundary (年/月/日).
 * At these boundaries, backspace should first hide the suffix before removing a digit.
 */
export function isAtSuffixBoundary(
  digitCount: number,
  mode: 'day' | 'month' | 'year'
): boolean {
  if (mode === 'year') return digitCount === 3;
  if (mode === 'month') return digitCount === 3 || digitCount === 5;
  return digitCount === 3 || digitCount === 5 || digitCount === 7;
}

/**
 * Returns the maximum number of raw digits for the given mode.
 */
export function getWarekiMaxDigits(mode: 'day' | 'month' | 'year'): number {
  if (mode === 'year') return 3;
  return mode === 'month' ? 5 : 7;
}

/** Reverse map: era offset → era digit */
const OFFSET_TO_DIGIT: Record<number, number> = {
  2018: 5, // 令和
  1988: 4, // 平成
  1925: 3, // 昭和
  1911: 2, // 大正
  1867: 1, // 明治
};

/** Era boundary table for reverse lookup (newest first) */
const ERA_BOUNDARIES: { offset: number; start: Date }[] = [
  { offset: 2018, start: new Date(2019, 4, 1) }, // 令和
  { offset: 1988, start: new Date(1989, 0, 8) }, // 平成
  { offset: 1925, start: new Date(1926, 11, 25) }, // 昭和
  { offset: 1911, start: new Date(1912, 6, 30) }, // 大正
  { offset: 1867, start: new Date(1868, 0, 25) }, // 明治
];

/** Max wareki year per era (1=明治,2=大正,3=昭和,4=平成). 令和(5) is dynamic from current year. */
function getEraMaxYear(eraDigit: number): number {
  // Spec: Reiwa is limited up to 令和39 (do not allow 40+).
  if (eraDigit === 5) return 39;
  const fixed: Record<number, number> = { 1: 45, 2: 15, 3: 64, 4: 31 };
  return fixed[eraDigit] ?? 99;
}

export function isWarekiYearValid(eraDigit: number, yy: number): boolean {
  if (yy < 1 || yy > 99) return false;
  return yy <= getEraMaxYear(eraDigit);
}

export function isMonthValid(mm: number): boolean {
  return mm >= 1 && mm <= 12;
}

export function isDayValid(dd: number): boolean {
  return dd >= 1 && dd <= 31;
}

/**
 * Returns true if appending nextDigit to current rawDigits would keep all completed segments valid.
 * Used to block invalid year/month/day input.
 */
export function canAppendWarekiDigit(
  current: string,
  nextDigit: string,
  mode: 'day' | 'month' | 'year'
): boolean {
  const next = current + nextDigit;
  const eraDigit = parseInt(next[0], 10);
  if (eraDigit < 1 || eraDigit > 5) return false;
  if (next.length === 3) {
    const yy = parseInt(next.slice(1, 3), 10);
    return isWarekiYearValid(eraDigit, yy);
  }
  if (next.length === 5) {
    const mm = parseInt(next.slice(3, 5), 10);
    return isMonthValid(mm);
  }
  if (next.length === 7 && mode === 'day') {
    const dd = parseInt(next.slice(5, 7), 10);
    return isDayValid(dd);
  }
  return true;
}

/**
 * Convert a Date back to the raw digit string (EYYMMDD for day, EYYMM for month).
 * Returns '' if the date cannot be mapped to a known era.
 */
export function dateToRawDigits(
  d: Date,
  mode: 'day' | 'month' | 'year' = 'day'
): string {
  if (!d || Number.isNaN(d.getTime())) return '';
  const ts = d.getTime();
  for (const era of ERA_BOUNDARIES) {
    if (ts >= era.start.getTime()) {
      const eraDigit = OFFSET_TO_DIGIT[era.offset];
      const warekiYear = String(d.getFullYear() - era.offset).padStart(2, '0');
      if (mode === 'year') return `${eraDigit}${warekiYear}`;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      if (mode === 'month') return `${eraDigit}${warekiYear}${mm}`;
      const dd = String(d.getDate()).padStart(2, '0');
      return `${eraDigit}${warekiYear}${mm}${dd}`;
    }
  }
  return '';
}

export { convertWareki, convertWarekiMonth, convertSeireki };
